/**
 * Call — a plain link, so a phone (or the home-screen app) opens its dialer. Numbers are dialled as digits;
 * the label is the word "Call" with the person's name for screen readers.
 *
 * 1.5.7: every call link in the signed-in app comes from here or from `useCallLink()` (src/lib/calling.ts),
 * so it dials the way the person chose — normally, with their number hidden (*67 first), or through Teams —
 * and says so when it is not a normal call. The number shown never changes.
 */
import type { AnchorHTMLAttributes, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useCallLink } from '@/lib/calling';
import { Icon } from './Icons';

export function CallButton({ phone, name, className, ghost }: { phone: string | null; name: string; className?: string; ghost?: boolean }) {
  const { t } = useTranslation();
  const link = useCallLink();
  if (!phone) return null;
  return (
    <a className={cn('btn sm call', ghost ? 'ghost' : 'secondary', className)} {...link(phone, t('contact.callName', { name }))}>
      <Icon name="phone" />
      <span>{t('contact.call')}</span>
    </a>
  );
}

/**
 * A phone number (or anything else) as a call link. `label` is the link's accessible name when the visible
 * words are not enough ("Call · 4B · SECURITY-TEST Tenant"); without children the number itself is shown.
 */
export function CallLink({ phone, label, children, ...rest }: { phone: string; label?: string; children?: ReactNode } & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'title' | 'aria-label'>) {
  const link = useCallLink();
  return (
    <a {...rest} {...link(phone, label)}>
      {children ?? phone}
    </a>
  );
}
